import { t, Trans } from '@lingui/macro'
import {
  Alert,
  AlertTitle,
  Button,
  CircularProgress,
  Collapse,
  Dialog,
  DialogContent,
  Grid,
  Icon,
  List,
  ListItem,
  Paper,
  Typography
} from '@mui/material'
import {
  setAvaliableOrganizations,
  setOrganization
} from 'app/redux/actions/OrganizationActions'
import {
  askToJoinOrganizationByFlow,
  createOrganizationByFlow,
  getAccountParsedById,
  NO_ORGANIZATION_FOUND_ERROR,
  searchForAccountByFlow,
  searchForAccountRawByFlow,
  TOO_MANY_RECORDS_FOUND_ERROR
} from 'app/services/sfAuth/sfData/sfAccount'
import { getJoinRequestCountByFlow } from 'app/services/sfAuth/sfData/sfJoinRequest'
import { TextFieldWithFormik } from 'app/views/common-components/with-formik/TextFieldWithFormik'
import { Formik, useFormikContext } from 'formik'
import { useSnackbar } from 'notistack'
import { useEffect, useRef } from 'react'
import NumberFormat from 'react-number-format'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { myI18n } from 'translation/I18nConnectedProvider'
import * as Yup from 'yup'
import {
  saveUser,
  saveUserParsed
} from '../../../services/sfAuth/sfData/sfUser'
import ProgressSnackbar from '../../common-components/CustomSnackbars'
import { countHelperText } from '../../common-components/FormElement'
import {
  emailTrans,
  phoneTrans,
  requiredSite,
  requiredTrans
} from '../../forms/formTranslations'
import './JoinOrganization.css'

const styles = {
  header: {
    // fontSize: '42px',
    fontWeight: 'bold',
    // textAlign: 'center',
    padding: 20
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    height: '40px'
  }
}

export const errorsToTranslation = {
  [TOO_MANY_RECORDS_FOUND_ERROR]: myI18n._(
    t`Too many records found! Try to be more specific in your search`
  ),
  [NO_ORGANIZATION_FOUND_ERROR]: myI18n._(t`No organisation found!`)
}

/** Component which allows user to search for an organization and join it or create a new one
 * @component
 * @returns {JSX.Element}
 */
const JoinOrganization = () => {
  const nameRef = useRef(null)

  const resultsRef = useRef(null)

  useEffect(() => {
    nameRef?.current?.focus()
  }, [nameRef.current])

  const { enqueueSnackbar } = useSnackbar()

  const avaliableOrganizations = useSelector(
    state => state.avaliableOrganizations
  )

  return (
    <Formik
      initialValues={{
        name: '', // organization name. used for search and create
        mail: '', // organization email. used for search
        website: '', // organization website. used for search
        phone: '', // organization phone. used for search
        jobTitle: '', // job title. used when joining organization
        loading: false, // loading flag
        results: null, // search results
        wantsToCreate: false,
        expanded: false, // flag for showing create organization form
        connectedOrganization: null, // organization to which user is connecting. This data is used in join organization dialog
        connecting: false, // flag for connecting to organization
        newOrganization: '', // new organization name
        joinOrganizationIds: [] // orgsanization ids for which user has join requests
      }}
      validationSchema={Yup.object().shape({
        wantsToCreate: Yup.boolean(),
        name: Yup.string().required(requiredTrans),
        website: Yup.string().default('').ensure().url(requiredSite),
        phone: Yup.string().test(
          'isValidPhone',
          () => phoneTrans,
          value => {
            if (value) {
              const filtered = value.replace(/[^0-9]/g, '')
              return filtered.length >= 10
            } else {
              return true
            }
          }
        ),
        mail: Yup.string().ensure().email(emailTrans),
        newOrganization: Yup.string().when(
          'wantsToCreate',
          (wantsToCreate, schema) =>
            wantsToCreate ? schema.required(requiredTrans) : schema
        )
      })}
    >
      {({
        values,
        dirty,
        isValid,
        setFieldValue,
        setValues,
        errors,
        touched,
        setFieldTouched
      }) => {
        const {
          results,
          buttonHidden,
          expanded,
          joinOrganizationIds,
          loading,
          connectedOrganization
        } = values

        return (
          <div style={{ padding: 20 }}>
            <Paper style={{ padding: '32px' }}>
              <div style={{ textAlign: 'center', marginBottom: 40 }}>
                <Typography variant='h5'>
                  <b>
                    <Trans>JOIN_ORGANIZATION_MAIN_TITLE</Trans>
                  </b>
                </Typography>
              </div>
              <Typography style={{ marginBottom: 24 }}>
                <b>
                  <Trans>JOIN_ORGANIZATION_DONT_USE_ACRONYMS</Trans>
                </b>
              </Typography>

              <Grid container direction='column' spacing={4}>
                <Grid item>
                  <TextFieldWithFormik
                    variant='outlined'
                    topLabel={<Trans>JOIN_ORGANIZATION_NAME_LABEL</Trans>}
                    name='name'
                    style={{ width: '100%' }}
                    inputRef={nameRef}
                    onChange={(e, value) => {
                      setFieldValue('name', e.target.value)
                      setFieldValue('newOrganization', e.target.value)
                    }}
                    required
                  />
                </Grid>
                <Grid item>
                  <TextFieldWithFormik
                    variant='outlined'
                    topLabel={<Trans>JOIN_ORGANIZATION_WEBSITE_LABEL</Trans>}
                    name='website'
                    style={{ width: '100%' }}
                  />
                </Grid>
                <Grid item>
                  <TextFieldWithFormik
                    variant='outlined'
                    topLabel={<Trans>JOIN_ORGANIZATION_EMAIL_LABEL</Trans>}
                    name='mail'
                    style={{ width: '100%' }}
                  />
                </Grid>
                <Grid item>
                  <TextFieldWithFormik
                    type='tel'
                    variant='outlined'
                    topLabel={<Trans>JOIN_ORGANIZATION_PHONE_LABEL</Trans>}
                    name='phone'
                    InputProps={{ inputComponent: PhoneFormat }}
                    style={{ width: '100%' }}
                  />
                </Grid>
              </Grid>

              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  margin: '40px 0 16px 0'
                }}
              >
                <Button
                  type='submit'
                  variant='contained'
                  color='primary'
                  disabled={
                    !dirty ||
                    (errors &&
                      Object.keys(errors).filter(key => key !== 'name').length >
                        0)
                  }
                  onClick={e => {
                    e.stopPropagation()
                    setValues({
                      ...values,
                      loading: true,
                      buttonHidden: null,
                      results: null
                    })
                    Promise.all([
                      searchForAccountByFlow({
                        name: values.name,
                        website: values.website,
                        email: values.mail,
                        phone: values.phone
                      }),
                      getJoinRequestCountByFlow()
                    ])
                      .then(([result, joinRequests]) => {
                        console.log('searched result', { result, joinRequests })
                        const joinOrganizationIds = joinRequests?.requests?.map(
                          item => item.AccountId__c
                        )
                        if (typeof result === 'string') {
                          if (!errorsToTranslation[result]) {
                            enqueueSnackbar(result, { variant: 'error' })
                          }

                          setValues({
                            ...values,
                            results: result,
                            buttonHidden: true,
                            joinOrganizationIds: null,
                            loading: false,
                            expanded: Boolean(errorsToTranslation[result]),
                            wantsToCreate: true
                          })
                        } else {
                          setValues({
                            ...values,
                            loading: false,
                            results: result,
                            buttonHidden: false,
                            wantsToCreate: false,
                            expanded: false,
                            joinOrganizationIds
                          })
                        }
                        resultsRef?.current?.scrollIntoView({
                          behavior: 'smooth'
                        })
                      })
                      .catch(error => {
                        console.log('searched error', { error })
                        enqueueSnackbar(
                          <Trans>
                            JOIN_ORGANIZATION_POSSIBLE_TOO_MANY_RESULTS_ERROR
                          </Trans>,
                          { variant: 'error' }
                        )
                        setValues({
                          ...values,
                          loading: false
                        })
                      })
                  }}
                >
                  <Typography>
                    <Trans>Search</Trans>
                  </Typography>
                </Button>
              </div>

              {loading && (
                <div style={{ marginTop: 20, marginBottom: 20 }}>
                  <div style={styles.loading}>
                    <CircularProgress />
                  </div>
                </div>
              )}

              {connectedOrganization && <JoinOrganizationDialog />}

              <div ref={resultsRef}>
                {results && (
                  <>
                    {Array.isArray(results) && results.length !== 0 && (
                      <div className='join-organization-results'>
                        <Typography variant='subtitle1'>
                          <b>
                            {results.length > 5
                              ? (
                                <Trans>JOIN_ORGANIZATION_TOP_RESULTS</Trans>
                                )
                              : (
                                <Trans>Results</Trans>
                                )}
                          </b>
                        </Typography>
                        <Typography style={{ margin: '16px 0' }}>
                          <Trans>
                            JOIN_ORGANIZATION_SELECT_ORGANIZATION_TEXT
                          </Trans>
                        </Typography>
                        <List className='join-organization-results-items'>
                          {results.slice(0, 5).map((item, index) => {
                            const alreadyConnected =
                              avaliableOrganizations.find(
                                org => org.id === item.Id
                              )

                            const onClick = () => {
                              if (!alreadyConnected && !hasJoinRequest) { setFieldValue('connectedOrganization', item) }
                            }

                            const hasJoinRequest =
                              joinOrganizationIds &&
                              joinOrganizationIds.includes(item.Id)

                            return (
                              <ListItem
                                className={`join-organization-result-item ${
                                  alreadyConnected || hasJoinRequest
                                    ? 'warning'
                                    : ''
                                }`}
                                key={item.Id}
                                button={!alreadyConnected && !hasJoinRequest}
                              >
                                <div
                                  className='join-organization-result-item-content'
                                  onClick={onClick}
                                >
                                  <div>
                                    {item.Name}
                                    <div>
                                      <i>{item.Website}</i>
                                    </div>
                                  </div>
                                  {alreadyConnected && (
                                    <div className='join-organization-connect-warning'>
                                      <Alert severity='warning'>
                                        <Trans>
                                          JOIN_ORGANIZATION_ALREADY_CONNECTED_SUBTITLE
                                        </Trans>
                                      </Alert>
                                    </div>
                                  )}
                                  {hasJoinRequest && (
                                    <div className='join-organization-connect-warning'>
                                      <Alert severity='warning'>
                                        <Trans>
                                          JOIN_ORGANIZATION_ALREADY_REQUESTED_SUBTITLE
                                        </Trans>
                                      </Alert>
                                    </div>
                                  )}
                                  {!alreadyConnected && !hasJoinRequest && (
                                    <div className='join-organization-connect-btn'>
                                      <Trans>
                                        JOIN_ORGANIZATION_CONNECT_BTN
                                      </Trans>
                                      <Icon>east</Icon>
                                    </div>
                                  )}
                                </div>
                              </ListItem>
                            )
                          })}
                        </List>
                      </div>
                    )}
                    {errorsToTranslation[results] && (
                      <Alert
                        severity='warning'
                        style={{ paddingTop: 8, paddingBottom: 8 }}
                      >
                        <AlertTitle>{errorsToTranslation[results]}</AlertTitle>
                      </Alert>
                    )}
                    <div className='join-organization-after-search'>
                      {!buttonHidden && (
                        <Button
                          color='primary'
                          variant='outlined'
                          onClick={() => {
                            setValues({
                              ...values,
                              expanded: !expanded,
                              wantsToCreate: true
                            })
                          }}
                        >
                          <Trans>
                            JOIN_ORGANIZATION_NO_MY_ORGANIZATION_BTN
                          </Trans>
                        </Button>
                      )}

                      <Collapse
                        in={expanded || !results.length}
                        className='join-organization-after-search-collapse'
                      >
                        <CreateOrganization />
                      </Collapse>
                    </div>
                  </>
                )}
              </div>
            </Paper>
          </div>
        )
      }}
    </Formik>
  )
}

/** Component which formats phone number
 * @component
 * @param {object} props
 * @param {object} props.inputRef
 * @param {function} props.onChange
 * @param {string} props.name
 * @returns {JSX.Element}
 */
function PhoneFormat (props) {
  const { inputRef, onChange, ...other } = props
  return (
    <NumberFormat
      {...other}
      format='(+1) ###-###-####'
      mask='_'
      isNumericString
      type='tel'
      getInputRef={inputRef}
      allowNegative={false}
      onValueChange={values => {
        onChange({
          target: {
            name: props.name,
            value: values.value
          }
        })
      }}
    />
  )
}

/** lookup for messages to show in snackbar after requesting to join an organization */
export const connectToOrganizationFlowResults = {
  ERROR_ALREADY_ON_TEAM: {
    messege: <Trans>You are already a member of this team!</Trans>,
    setCurrent: true,
    type: 'error',
    edit: true
  },
  RESULT_BY_EMAIL_NO_USER_INVITE_SENT: {
    messege: (
      <Trans>
        No user found! Join request was created based on email provided
      </Trans>
    ),
    type: 'info',
    noAccountFetch: true,
    edit: false
  },
  RESULT_ADDED_AS_MANAGER: {
    messege: (
      <Trans>
        There was no team member in selected organization. You were set as
        organization manager
      </Trans>
    ),
    setCurrent: true,
    type: 'info',
    edit: true
  },
  RESULT_REQUEST_SENT: {
    messege: <Trans>The request to join this organization was created</Trans>,
    type: 'info',
    // noAccountFetch: true,
    notAvailable: true,
    edit: false
  },
  ERROR_ALREADY_REQUESTED: {
    messege: <Trans>You already requested to join this organization!</Trans>,
    type: 'error',
    noAccountFetch: true,
    edit: false
  }
}

/** Component which allows user to create a new organization
 * @component
 * @returns {JSX.Element}
 */
const CreateOrganization = () => {
  const user = useSelector(state => state.user)
  const organization = useSelector(state => state.organization)
  const avaliableOrganizations = useSelector(
    state => state.avaliableOrganizations
  )
  const dispatch = useDispatch()

  const history = useHistory()

  const { values, setFieldValue } = useFormikContext()
  const { newOrganization, creating } = values

  const { enqueueSnackbar, closeSnackbar } = useSnackbar()

  /** Function which searches for an organization by name and returns it
   * @param {string} name
   * @returns {Promise<object>}
   */
  const searchAccountByName = name => {
    return searchForAccountRawByFlow({ name: name.trim() }).then(result => {
      const organization = result.find(
        item => item.Name.trim().toLowerCase() === name.trim().toLowerCase()
      )
      return organization
    })
  }

  /** Function which creates a new organization and redirects user to it
   * @param {string} progressKey - key of progress snackbar
   * @returns {Promise<void>}
   * @throws {Error} DUPLICATES_DETECTED
   * @throws {Error} Error Contact Administrator
   */
  const createOrganizationPromise = (progressKey, jobTitle) => {
    createOrganizationByFlow(user.userObject, {
      displayName: user.displayName,
      username: user.userInfo.username,
      jobTitle: values.jobTitle,
      organizationName: values.newOrganization
    })
      .then(orgResult => {
        if (orgResult === organization.id) {
          enqueueSnackbar(
            <Trans>An organization for this user already exits!</Trans>,
            {
              variant: 'error'
            }
          )
        } else {
          enqueueSnackbar(<Trans>New organization created!</Trans>, {
            variant: 'success'
          })
        }

        return Promise.all([
          getAccountParsedById(orgResult, {
            permissions: { TEAM_MEMBERS: true }
          }),
          saveUser({
            Id: user.userId,
            Associated_Organizations__c: orgResult
          })
        ]).then(([org]) => {
          setFieldValue('creating', false)
          dispatch(
            setAvaliableOrganizations([
              ...avaliableOrganizations,
              { id: org.id, name: org.organisationsName }
            ])
          )
          dispatch(setOrganization({ ...org }))
          closeSnackbar(progressKey)
          history.push('/grants/organization-details')
        })
      })
      .catch(error => {
        if (error.message === 'DUPLICATES_DETECTED') {
          enqueueSnackbar(
            <Trans>
              A organization for this user exits - try searching with your first
              and last name in name of organization{' '}
            </Trans>,
            {
              variant: 'error'
            }
          )
          return
        }
        console.error('could not create org', error)
        closeSnackbar(progressKey)
        enqueueSnackbar(<Trans>Error Contact Administrator</Trans>, {
          variant: 'error'
        })
      })
  }

  return (
    <>
      <div style={{ width: '100%' }}>
        {(!values.results?.length ||
          values.results === 'NO_ORGANIZATION_FOUND_ERROR') && (
            <Typography variant='subtitle1' style={{ padding: '16px 0 24px 0' }}>
              <b>
                <Trans>JOIN_ORGANIZATION_NO_RESULT</Trans>
              </b>
            </Typography>
        )}
        {(values.results?.length > 0 ||
          values.results === 'NO_ORGANIZATION_FOUND_ERROR') && (
            <Typography variant='subtitle1' style={{ padding: '16px 0 24px 0' }}>
              <b>
                <Trans>
                  JOIN_ORGANIZATION_CREATE_NEW_ORGANIZATION_TITLE
                  {values.newOrganization}
                </Trans>
                ?
              </b>
            </Typography>
        )}

        {values.results !== 'NO_ORGANIZATION_FOUND_ERROR' &&
          values.results !== 'TOO_MANY_RECORDS_FOUND_ERROR' && (
            <Typography style={{ padding: '16px 0 24px 0' }}>
              <b>
                <Trans>
                  JOIN_ORGANIZATION_DOUBLE_CHECK_NEW_ORGANIZATION_SUBTITLE
                </Trans>
              </b>
            </Typography>
        )}

        <TextFieldWithFormik
          variant='outlined'
          topLabel={<Trans>JOIN_ORGANIZATION_NEW_ORGANIZATION_LABEL</Trans>}
          required
          name='newOrganization'
          style={{ width: '100%' }}
        />

        <TextFieldWithFormik
          variant='outlined'
          topLabel={<Trans>Job title</Trans>}
          required={false}
          name='jobTitle'
          style={{ width: '100%' }}
        />
      </div>

      <div className='join-organization-create-organizaion-btn-wrapper'>
        <Button
          variant='contained'
          color='primary'
          disabled={creating || !newOrganization}
          onClick={() => {
            setFieldValue('creating', true)
            const progressKey = enqueueSnackbar('', {
              persist: true,
              content: key =>
                ProgressSnackbar(<Trans>Creating new organisation</Trans>)
            })
            searchAccountByName(newOrganization)
              .then(result => {
                if (!result) {
                  createOrganizationPromise(progressKey, values.jobTitle)
                } else {
                  closeSnackbar(progressKey)
                  enqueueSnackbar(
                    <Trans>Organization with this name already exists!</Trans>,
                    { variant: 'error' }
                  )
                  setFieldValue('creating', false)
                  setFieldValue('connectedOrganization', result)
                }
              })
              .catch(error => {
                if (error !== 'NO_ORGANIZATION_FOUND_ERROR') {
                  closeSnackbar(progressKey)
                  enqueueSnackbar(
                    <Trans>JOIN_ORGANIZATION_CREATE_ERROR</Trans>,
                    {
                      variant: 'error'
                    }
                  )
                  setFieldValue('creating', false)
                }
              })
          }}
        >
          <Trans>Create new organisation</Trans>
        </Button>
      </div>
    </>
  )
}

/** Component which allows user to join an organization
 * @component
 * @returns {JSX.Element}
 */
const JoinOrganizationDialog = () => {
  const { values, setFieldValue, setValues, errors } = useFormikContext()
  const {
    jobTitle,
    connectedOrganization: item,
    connecting,
    joinOrganizationIds
  } = values
  const avaliableOrganizations = useSelector(
    state => state.avaliableOrganizations
  )
  const user = useSelector(state => state.user)
  const { userId } = user
  const dispatch = useDispatch()
  const jobTitleRef = useRef(null)

  useEffect(() => {
    jobTitleRef?.current?.focus()
  }, [jobTitleRef.current])

  const { enqueueSnackbar, closeSnackbar } = useSnackbar()

  const history = useHistory()

  const handleError = () => {
    setFieldValue('connecting', false)
  }
  const handleConnect = () => {
    setFieldValue('connecting', true)
  }
  const onClose = joinOrganizationIds => {
    setValues({
      ...values,
      connectedOrganization: null,
      connecting: false,
      jobTitle: '',
      joinOrganizationIds: joinOrganizationIds || values.joinOrganizationIds
    })
  }

  if (!item) {
    return null
  }

  return (
    <Dialog open={!!item} className='join-organization-dialog'>
      <DialogContent className='join-organization-dialog-content'>
        <div className='join-organization-dialog-close-btn'>
          <Icon onClick={() => onClose()}>close</Icon>
        </div>

        <>
          <Typography
            variant='subtitle1'
            className='join-organization-dialog-title'
          >
            <b>
              <Trans>JOIN_ORGANIZATION_DIALOG_TITLE</Trans>
            </b>
          </Typography>

          <Typography
            variant='subtitle1'
            className='join-organization-dialog-organization-name'
          >
            <b>{item.Name}</b>
          </Typography>
          <div style={{ textAlign: 'center', paddingBottom: 16 }}>
            <i>{item.Website}</i>
          </div>

          {/* <List className='join-organization-dialog-list'>
            <ListItem>
              <Typography>
                <b>
                  <Trans>Website</Trans>:
                </b>{' '}
                {item.Website}
              </Typography>
            </ListItem>
          <ListItem>
              <Typography>
                <b>
                  <Trans>E-mail</Trans>:
                </b>{' '}
                {item.Email__c}
              </Typography>
            </ListItem>
            <ListItem>
              <Typography>
                <b>
                  <Trans>JOIN_ORGANIZATION_PHONE_NUMBER</Trans>:
                </b>{' '}
                {item.Phone}
              </Typography>
            </ListItem>
          </List> */}

          <TextFieldWithFormik
            name='jobTitle'
            topLabel={<Trans>Your job title</Trans>}
            fullWidth
            variant='outlined'
            inputProps={{
              maxLength: 80
            }}
            helperText={
              <span key='limit'>
                {countHelperText(jobTitle, 80)}
                {'. '}
              </span>
            }
            required
            inputRef={jobTitleRef}
          />

          <Grid container className='join-organization-dialog-connect-btn'>
            <Button
              type='submit'
              disabled={connecting}
              variant='contained'
              color='primary'
              onClick={e => {
                e.stopPropagation()
                if (errors.jobTitle) {
                  return jobTitleRef?.current?.focus()
                }
                handleConnect()
                const progressKey = enqueueSnackbar('', {
                  persist: true,
                  content: key => ProgressSnackbar(<Trans>Updating info</Trans>)
                })
                askToJoinOrganizationByFlow({
                  jobTitle,
                  userId,
                  orgId: item.Id
                }).then(
                  flowResult => {
                    const connectData =
                      connectToOrganizationFlowResults[
                        flowResult[0].outputValues.result
                      ]
                    let joinOrganizationIds = values.joinOrganizationIds || []
                    if (
                      flowResult[0].outputValues.result ===
                      'RESULT_REQUEST_SENT'
                    ) {
                      joinOrganizationIds = [...joinOrganizationIds, item.Id]
                    }
                    if (connectData) {
                      enqueueSnackbar(connectData.messege, {
                        variant: connectData.type
                      })
                    } else {
                      enqueueSnackbar(<Trans>Info updated!</Trans>, {
                        variant: 'success'
                      })
                    }
                    let saveUserPromise
                    if (connectData.setCurrent) {
                      saveUserPromise = saveUserParsed({
                        id: userId,
                        associatedOrganizations: item.Id
                      })
                    } else {
                      saveUserPromise = Promise.resolve()
                    }
                    if (connectData.noAccountFetch) {
                      closeSnackbar(progressKey)
                      setFieldValue('results', null)
                      onClose(joinOrganizationIds)
                      return Promise.resolve()
                    }
                    return Promise.all([
                      saveUserPromise,
                      getAccountParsedById(item.Id, {
                        permissions: { TEAM_MEMBERS: true }
                      })
                    ]).then(
                      ([userSaved, org]) => {
                        closeSnackbar(progressKey)
                        setFieldValue('results', null)
                        if (
                          !avaliableOrganizations.some(
                            org => org.id === item.Id
                          )
                        ) {
                          dispatch(
                            setAvaliableOrganizations([
                              ...avaliableOrganizations,
                              {
                                id: item.Id,
                                name: org.organisationsName
                              }
                            ])
                          )
                        }
                        dispatch(setOrganization({ ...org }))
                        onClose()
                        if (connectData.edit) {
                          history.push('/grants/organization-details')
                        }
                      },
                      reject => {
                        console.log(reject)
                        handleError()
                      }
                    )
                  },
                  error => {
                    console.log(error)
                    handleError()
                  }
                )
              }}
            >
              <Trans>Associate with this organisation</Trans>
            </Button>
          </Grid>
        </>
      </DialogContent>
    </Dialog>
  )
}

export default JoinOrganization
